import { isNavigationFailure, NavigationFailureType, useRoute, useRouter } from 'vue-router';
import { toReactive } from '@vueuse/core';
import chroma from 'chroma-js';
import { useI18n } from 'vue-i18n';
import { i18n } from '@/i18n/i18n';
import { useTheme } from '@@/packages/shared/composables/theme';
import { useTags } from '@/modules/tags/api';
import { useAvailableLabels } from '@/modules/labels/api';
import type { NetworkFilters } from './types';
import type {
  ClientNetworkResponse,
  CommunityNetworkOrganisation,
  CommunityNetworkResponse,
  NetworkOrganisation,
} from './api';

// formatOrdinals returns the ordinal number for a degree of a supplier.
//
// e.g:
// '3rd' if n = 3
// '1st' if n = 1
export const formatOrdinals = (n: number) => {
  const pr = new Intl.PluralRules(i18n.global.locale, { type: 'ordinal' });
  const rule = pr.select(n);
  const ordinal = i18n.global.t(`clientNetwork.ordinals.${rule}`);
  return i18n.global.t('clientNetwork.ordinalNumber', { number: n, ordinal });
};

const pr = new Intl.PluralRules(undefined, { type: 'ordinal' });
const suffixes = new Map([
  ['one', 'st'],
  ['two', 'nd'],
  ['few', 'rd'],
  ['other', 'th'],
]);

// Why on earth doesn't vue-i18n do this?! :(
export const ordinalForNumber = (n: number) => {
  const rule = pr.select(n);
  const suffix = suffixes.get(rule);
  return `${n}${suffix}`;
};

export const calculateNthDegree = (
  network: ClientNetworkResponse | CommunityNetworkResponse | undefined,
) => {
  if (!network) return null;

  // Get all degrees of current nodes
  const degreesByID = Object.fromEntries(
    (Object.values(network.organisations) as NetworkOrganisation[] | CommunityNetworkOrganisation[])
      // Ignore root client node
      .filter((org) => org.degree !== 0)
      .map((org) => [org.id, org.degree] as const),
  );

  // Add anonymous orgs to the degreesByID map using the connections.
  // Prioritise the lower degree if the org appears multiple times.
  Object.values(network.connections).forEach((conn) => {
    const orgDegree = degreesByID[conn.supplierID];

    if (!orgDegree || conn.degree < orgDegree) {
      degreesByID[conn.supplierID] = conn.degree;
    }
  });

  // Count the occurrences for each degree.
  const counts = Object.values(degreesByID).reduce(
    (obj, degree) => {
      if (!obj[degree]) obj[degree] = 1;
      else obj[degree] += 1;

      return obj;
    },
    {} as Record<string, number>,
  );

  if (!Object.keys(counts).length) {
    return null;
  }

  return counts;
};

export const calculateMaxNthDegree = (nthDegree: ReturnType<typeof calculateNthDegree>) => {
  if (!nthDegree) return undefined;
  const stringNumber = Object.keys(nthDegree).pop();
  if (!stringNumber) return undefined;
  return Number(stringNumber) + 2;
};

const CONCENTRATION_RISK_MIN_CLIENTS = 3;

// New array concentrationRiskOrgIDs, including the org ids of possible concentration risks.
// An org is a possible concentration risk if it has 3 or more client connections.
export const getConcentrationRiskOrgIDs = (
  network: ClientNetworkResponse | CommunityNetworkResponse | undefined,
) => {
  if (!network) return [];

  return Object.entries(
    Object.values(network.connections).reduce<Record<string, number>>((res, conn) => {
      res[conn.supplierID] = (res[conn.supplierID] || 0) + 1;
      return res;
    }, {}),
  )
    .filter(([, val]) => val >= CONCENTRATION_RISK_MIN_CLIENTS)
    .map(([key]) => key);
};

export const useNetworkColors = () => {
  const theme = useTheme();

  return toReactive(
    computed(() => {
      const isDark = theme.isDarkDangerous.value;
      const { network } = theme;
      return {
        // The user's own organisation (the root node) — previously had no
        // dedicated colour and silently fell into the fourthParty bucket.
        organisation: network.node.organisation,
        thirdParty: {
          default: network.node.thirdParty.default,
          selected: network.node.thirdParty.selected,
          // No `network/*` token exists for this state yet — left on the raw
          // palette until design adds one.
          secondary: theme.colors.neutral400,
          unselected: network.node.thirdParty.unselected,
          concentrationRisk: {
            default: network.node.concentrationRisk.default,
            unselected: network.node.concentrationRisk.unselected,
          },
          // oxlint-disable-next-line import/no-named-as-default-member -- @types/chroma-js exposes scale only as a member of the default export
          compliance: chroma.scale(network.node.assessed).domain([0, 100]),
        },
        fourthParty: {
          default: network.node.fourthParty.default,
          selected: network.node.fourthParty.selected,
          // No `network/*` token exists for this state yet — left on the raw
          // palette until design adds one.
          secondary: isDark ? theme.colors.neutral500 : theme.colors.neutral300,
          unselected: network.node.fourthParty.unselected,
        },
        community: {
          default: network.node.community.default,
          unselected: network.node.community.unselected,
        },
        links: {
          default: network.connector.default,
          selected: network.connector.selected,
          secondary: network.connector.secondary,
          // Faded wash of the generic connector colour — blends into the
          // canvas background instead of reading as a fixed grey line.
          unselected: chroma(network.connector.default).alpha(0.3).hex(),
        },
      };
    }),
  );
};

// Opacity for a primary highlight `excess` hops beyond the shortest path to
// root: fades 30% per hop, floored at 40%, so detours recede without vanishing.
export function primaryDepthAlpha(excess: number): number {
  return Math.max(0.4, 1 - excess * 0.3);
}

// isOrgMatchingFilters returns whether an organisation should matches the
// currently-applied network filters.
export function isOrgMatchingFilters(
  org: NetworkOrganisation | CommunityNetworkOrganisation,
  filters: NetworkFilters | undefined,
) {
  if (!filters) {
    return true;
  }

  if (filters.criticality.length > 0 && !filters.criticality.includes(org.criticalityTagID)) {
    return false;
  }

  if (
    filters.confidentiality.length > 0 &&
    !filters.confidentiality.includes(org.confidentialityTagID)
  ) {
    return false;
  }

  if (filters.pii.length > 0 && !filters.pii.includes(org.holdsPIITagID)) {
    return false;
  }

  if (filters.labels.length > 0 && !filters.labels.some((l) => org.labelIDs.includes(l))) {
    return false;
  }

  if (
    filters.nthParty &&
    (org.degree < filters.nthParty[0] - 2 || org.degree > filters.nthParty[1] - 2)
  ) {
    return false;
  }

  return true;
}

export function getNthPartiesFilterText(nthParties: [number, number]) {
  const { t } = useI18n();
  if (nthParties[0] === nthParties[1]) {
    return t('clientNetwork.filters.aSuppliersOnly', {
      a: formatOrdinals(nthParties[0]),
    });
  }
  return t('clientNetwork.filters.aToBSuppliers', {
    a: formatOrdinals(nthParties[0]),
    b: formatOrdinals(nthParties[1]),
  });
}

const DEFAULT_MAX_DEGREE = 3;
const DEEPEST_MAX_DEGREE = 7;

// Fixed, not derived from the response: depth bounds the fetch, and a short
// response doesn't prove a short network, since a withheld supply chain or the
// hide-Risk-Ledger prune truncates the traversal the same way. Narrowing this
// needs the backend to surface the true depth.
export const networkDepthOptions = (label: (degree: number) => string) =>
  Array.from({ length: DEEPEST_MAX_DEGREE }, (_, index) => ({
    label: label(index + 1),
    value: index + 1,
  }));

export const nthPartyDepthOptions = () =>
  networkDepthOptions((degree) =>
    i18n.global.t('clientNetwork.nthPartyOnly', { nth: formatOrdinals(degree + 2) }),
  );

// Merges into the existing query rather than replacing it. A duplicated failure
// means nothing actually changed, and a cancelled one means a second change
// landed first; both are the expected outcome of a URL-backed control, so only
// anything else is worth reporting.
const useReplaceQuery = () => {
  const router = useRouter();
  const route = useRoute();

  return (params: Record<string, string | undefined>) => {
    router.replace({ query: { ...route.query, ...params } }).catch((e) => {
      const expected =
        isNavigationFailure(e, NavigationFailureType.duplicated) ||
        isNavigationFailure(e, NavigationFailureType.cancelled);
      if (!expected) logError(e);
    });
  };
};

// Depth and the Risk Ledger toggle live in the URL so they survive a reload and
// drive the fetch. Animations stay local: they change nothing about what is fetched.
export const useNetworkPerformanceSettings = () => {
  const route = useRoute();
  const replaceQuery = useReplaceQuery();

  const depthLimit = computed({
    // Clamped, not just validated: a stale or hand-edited ?depth=1000 would
    // otherwise be forwarded verbatim and ask for the whole traversal.
    get: () => {
      const depth = Number(route.query.depth);
      if (!Number.isInteger(depth) || depth < 1) return DEFAULT_MAX_DEGREE;
      return Math.min(depth, DEEPEST_MAX_DEGREE);
    },
    set: (value) => replaceQuery({ depth: `${value}` }),
  });

  // Shown by default, so the URL only ever carries the opted-out state.
  const showRiskLedgerSuppliers = computed({
    get: () => route.query.hideRiskLedgerSuppliers !== 'true',
    set: (value) => replaceQuery({ hideRiskLedgerSuppliers: value ? undefined : 'true' }),
  });

  // The backend reads an absent depth as the whole network, so the product
  // default has to be sent explicitly.
  const requestOptions = computed(() => ({
    depth: `${depthLimit.value}`,
    ...(!showRiskLedgerSuppliers.value && { hideRiskLedgerSuppliers: 'true' }),
  }));

  return { depthLimit, showRiskLedgerSuppliers, animationsEnabled: ref(true), requestOptions };
};

export const useFilters = () => {
  const { tags } = useTags();
  const { labelsByID } = useAvailableLabels('supplier');

  const getInitialFilters = () => ({
    criticality: [],
    confidentiality: [],
    pii: [],
    labels: [],
    nthParty: undefined,
  });

  const filters = ref<NetworkFilters>(getInitialFilters());

  const resetFilters = () => {
    Object.assign(filters.value, getInitialFilters());
  };

  const filtersApplied = computed(() =>
    Object.values(filters.value).some((filter) => {
      if (!filter) return false;
      if (Array.isArray(filter)) return !!filter.length;
      return !!filter;
    }),
  );

  const filterNames = computed(() => {
    if (!filtersApplied.value) {
      return [];
    }

    const names = [
      ...tags.value
        .filter((t) =>
          [
            ...(filters.value.criticality ? filters.value.criticality : []),
            ...(filters.value.confidentiality ? filters.value.confidentiality : []),
            ...(filters.value.pii ? filters.value.pii : []),
          ].includes(t.tagID),
        )
        .map((t) => t.tag),
      ...Object.values(labelsByID.value)
        .filter((l) => filters.value.labels?.includes(l.id))
        .map((l) => l.name),
    ];

    if (filters.value.nthParty) {
      names.push(getNthPartiesFilterText(filters.value.nthParty));
    }

    return names;
  });

  const route = useRoute();
  const replaceQuery = useReplaceQuery();

  function setURLParams() {
    replaceQuery(
      Object.fromEntries(
        Object.entries(filters.value).map(([key, value]) => {
          if (!value) return [key, undefined];
          if (Array.isArray(value)) return [key, value.join(',') || undefined];
          return [key, value];
        }),
      ),
    );
  }
  watch(filters, setURLParams, { deep: true });

  function getFiltersFromURLParams(): NetworkFilters {
    const parseTags = (type: string) =>
      (route.query[type] as string | undefined)
        ?.split(',')
        .filter((v) => tags.value.find((t) => t.tagID === v && t.type === type)) || [];

    const nthParty =
      typeof route.query.nthParty === 'string'
        ? route.query.nthParty.split(',').map(Number)
        : undefined;

    return {
      criticality: parseTags('criticality'),
      confidentiality: parseTags('confidentiality'),
      pii: parseTags('pii'),
      labels:
        (route.query.labels as string | undefined)
          ?.split(',')
          .filter((v) => Object.keys(labelsByID.value).includes(v)) || [],
      nthParty: nthParty?.length === 2 ? (nthParty as [number, number]) : undefined,
      name: route.query.name ? (['false'] as const) : undefined,
      logo: route.query.logo ? (['false'] as const) : undefined,
    };
  }
  watch(tags, () => {
    filters.value = getFiltersFromURLParams();
  });

  return { filters, resetFilters, filtersApplied, filterNames };
};
